import { useState } from 'react';
import { Box, Typography, Button, Snackbar, Alert } from '@mui/material';
import { Add, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { newsService, NewsStatus } from '../services/news.service';
import { News } from '../types';
import { NewsTable, NewsToolbar } from '../components/news/NewsTable';
import { NewsForm } from '../components/news/NewsForm';

export default function NewsPage() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<NewsStatus | ''>('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingNews, setEditingNews] = useState<News | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const queryClient = useQueryClient();

  const {
    data: newsData,
    isLoading,
  } = useQuery({
    queryKey: ['news', { page, limit, search, statusFilter }],
    queryFn: () => {
      const filters: Record<string, any> = { page, limit, search };
      if (statusFilter) filters.status = statusFilter;
      return newsService.getList(filters);
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: Omit<News, 'id' | 'createdAt' | 'updatedAt'>) => newsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] });
      setIsFormOpen(false);
      setEditingNews(null);
      setNotification({ message: 'Новость успешно создана', type: 'success' });
    },
    onError: () => {
      setNotification({ message: 'Ошибка при создании новости', type: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<News> }) => newsService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] });
      setIsFormOpen(false);
      setEditingNews(null);
      setNotification({ message: 'Новость успешно обновлена', type: 'success' });
    },
    onError: () => {
      setNotification({ message: 'Ошибка при обновлении новости', type: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => newsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] });
      setNotification({ message: 'Новость удалена', type: 'success' });
    },
    onError: () => {
      setNotification({ message: 'Ошибка при удалении новости', type: 'error' });
    },
  });

  const publishMutation = useMutation({
    mutationFn: (id: number) => newsService.publish(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] });
      setNotification({ message: 'Новость опубликована', type: 'success' });
    },
    onError: () => {
      setNotification({ message: 'Ошибка при публикации новости', type: 'error' });
    },
  });

  const unpublishMutation = useMutation({
    mutationFn: (id: number) => newsService.unpublish(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] });
      setNotification({ message: 'Новость снята с публикации', type: 'success' });
    },
    onError: () => {
      setNotification({ message: 'Ошибка при снятии с публикации', type: 'error' });
    },
  });

  const filteredNews = newsData?.data || [];
  const total = newsData?.pagination?.total || 0;

  const handleCreate = () => {
    setEditingNews(null);
    setIsFormOpen(true);
  };

  const handleEdit = (news: News) => {
    setEditingNews(news);
    setIsFormOpen(true);
  };

  const handleSubmit = async (data: any) => {
    // Extract images from form data
    const { images, ...newsData } = data;
    
    if (editingNews) {
      await updateMutation.mutateAsync({ id: editingNews.id, data: { ...newsData, images } });
    } else {
      await createMutation.mutateAsync({ ...newsData, images });
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Вы уверены, что хотите удалить эту новость?')) {
      deleteMutation.mutate(id);
    }
  };

  const handlePublish = (id: number) => {
    publishMutation.mutate(id);
  };

  const handleUnpublish = (id: number) => {
    unpublishMutation.mutate(id);
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontFamily: 'Noto Serif' }}>
          Новости
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
          Добавить новость
        </Button>
      </Box>

      <NewsToolbar
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
        statusFilter={statusFilter}
        onStatusFilterChange={(value) => {
          setStatusFilter(value as NewsStatus | '');
          setPage(1);
        }}
      />

      <NewsTable
        news={filteredNews}
        loading={isLoading}
        total={total}
        page={page}
        limit={limit}
        onPageChange={setPage}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onPublish={handlePublish}
        onUnpublish={handleUnpublish}
      />

      <NewsForm
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleSubmit}
        initialData={editingNews as any}
        loading={createMutation.isPending || updateMutation.isPending}
        error={createMutation.error?.message || updateMutation.error?.message}
      />

      <Snackbar
        open={!!notification}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification?.type === 'success' ? 'success' : 'error'}
          sx={{ width: '100%' }}
          icon={notification?.type === 'success' ? <CheckCircle /> : <ErrorIcon />}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
